// Your portrait — the web rendering. Mirrors the iOS card home: the map,
// the deck of observations waiting for a word, what it remembers as cards
// by theme, the grow row, and quiet rows to the review list and privacy.
// No default lists; every failure is said out loud.

import { state } from "./state";
import { escapeHtml } from "./utils";
import {
  ARCHIVE_GROUP_TITLES,
  archiveGroup,
  awaitsWord,
  buildPortraitMap,
  claimById,
  compactLabel,
  deckClaims,
  evidenceSummary,
  firstExcerpt,
  growPrompt,
  isHistorical,
  keptCount,
  lastMentionedLine,
  mapLabelPositions,
  mapLayout,
  observationText,
  provenanceLabel,
  reflectiveQuestions,
  rememberedCards,
  stabilityDescription,
  themeOfClaim,
} from "./portrait";
import type { PortraitClaim } from "./types";

const MAP_W = 360;
const MAP_H = 220;

function eyebrow(text: string, trailing = ""): string {
  return `<div class="portrait-eyebrow"><span>${escapeHtml(text).toUpperCase()}</span>${trailing}</div>`;
}

function chevron(): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;
}

// ── Entry states ────────────────────────────────────────────────────────────

function renderIntro(): string {
  const p = state.portrait;
  return `
    <section class="panel portrait-panel">
      <div class="panel-header"><div><h2>Your portrait</h2></div></div>
      <div class="portrait-surface">
        <div class="portrait-intro-row"><strong>Your thoughts, made visible</strong><p>LOT can gather what your own writing suggests — what keeps coming up, what seems to matter — and show you every impression with the thoughts behind it.</p></div>
        <div class="portrait-intro-row"><strong>You stay the authority</strong><p>Nothing is treated as true about you until you've seen it. You can correct, reject, or delete anything, and your word permanently outranks the model.</p></div>
        <div class="portrait-intro-row"><strong>Private, always</strong><p>Your self-model is never shown to anyone else and is never used for matching or discovery without your separate, explicit choice. You can turn it off or erase it entirely at any time.</p></div>
      </div>
      <button class="btn-primary portrait-enable" data-action="portrait-enable" ${p.enabling ? "disabled" : ""}>${p.enabling ? "Building your first impressions…" : "Let LOT notice what keeps coming back"}</button>
      <p class="portrait-caption">Off by default. Turning it on builds your first impressions from thoughts you've already written.</p>
    </section>
  `;
}

function renderLoading(): string {
  return `
    <section class="panel portrait-panel portrait-loading" aria-label="Looking across your thoughts">
      <div class="portrait-loading-mark"><img src="/assets/app-store-icon.png" alt="" /></div>
      <p class="portrait-caption">Looking across your thoughts</p>
      <div class="portrait-skeleton" style="height:170px"></div>
      <div class="portrait-skeleton" style="height:52px"></div>
      <div class="portrait-skeleton" style="height:52px"></div>
    </section>
  `;
}

function renderFailed(): string {
  return `
    <section class="panel portrait-panel">
      <div class="portrait-surface">
        <p>LOT could not load your portrait right now. Your thoughts are safe.</p>
        <div class="portrait-actions-row">
          <button class="btn-secondary btn-sm" data-action="portrait-retry">Try again</button>
        </div>
      </div>
    </section>
  `;
}

function renderEarly(): string {
  return `
    <section class="panel portrait-panel">
      <div class="panel-header"><div><h2>Your portrait</h2></div></div>
      <div class="portrait-surface">
        <strong class="portrait-title">LOT is still learning</strong>
        <p class="portrait-body">There isn't enough in your journal yet to say anything worth your time. LOT won't guess who you are from a handful of entries.</p>
      </div>
      ${eyebrow("If you feel like reflecting")}
      <div class="portrait-rows">
        ${reflectiveQuestions().map((q) => `
          <button class="portrait-row" data-action="portrait-prompt" data-prompt="${escapeHtml(q)}">
            <span class="portrait-row-copy"><strong>${escapeHtml(q)}</strong></span>
            <span class="portrait-row-chevron">✎</span>
          </button>`).join("")}
      </div>
      <p class="portrait-caption">Optional. Writing about any of these gives future impressions something real to stand on.</p>
      ${renderQuietRows(false)}
    </section>
  `;
}

// ── The map ─────────────────────────────────────────────────────────────────

function renderMap(): string {
  const p = state.portrait;
  const model = buildPortraitMap();
  if (model.nodes.length < 3) return renderStructureLine(model.thoughtCount, model.themes.length);
  const points = mapLayout(model, MAP_W, MAP_H);
  const labels = mapLabelPositions(model, MAP_W, MAP_H);
  const focused = p.focusedNodeId;
  const following = p.selectedTheme;
  const themeIndex = new Map(model.themes.map((t, i) => [t, i]));
  const nodeById = new Map(model.nodes.map((n) => [n.id, n]));
  const touches = (e: { a: string; b: string }, id: string) => e.a === id || e.b === id;

  const nodeEmphasis = (n: typeof model.nodes[number]) => {
    if (focused) {
      if (n.id === focused) return 1;
      return model.edges.some((e) => touches(e, focused) && touches(e, n.id)) ? 0.85 : 0.22;
    }
    if (following) return n.theme === following ? 1 : 0.22;
    return 0.85;
  };
  const edgeEmphasis = (e: typeof model.edges[number]) => {
    if (focused) return touches(e, focused) ? 1 : 0.15;
    if (following) return (nodeById.get(e.a)?.theme === following || nodeById.get(e.b)?.theme === following) ? 0.9 : 0.15;
    return 0.6;
  };

  const edges = model.edges.map((e) => {
    const a = points.get(e.a); const b = points.get(e.b);
    if (!a || !b) return "";
    const em = edgeEmphasis(e);
    const cls = e.kind === "tension" ? "map-edge-tension" : e.kind === "link" ? "map-edge-link" : "map-edge-shared";
    return `<line class="map-edge ${cls}" x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}" style="opacity:${em.toFixed(2)}"/>`;
  }).join("");

  const nodes = model.nodes.map((n) => {
    const pt = points.get(n.id);
    if (!pt) return "";
    const em = nodeEmphasis(n);
    const isFocused = focused === n.id;
    const r = 3 + Math.min(4, n.weight * 0.8) + (isFocused ? 2 : 0);
    const hue = n.theme != null ? (themeIndex.get(n.theme) ?? 0) % 6 : -1;
    const phase = ((n.id.charCodeAt(0) + n.id.charCodeAt(n.id.length - 1)) % 7) * 0.9;
    return `
      <g class="map-node ${n.kept ? "is-kept" : "is-waiting"} ${n.quiet ? "is-quiet" : ""} ${isFocused ? "is-focused" : ""} map-theme-${hue}" data-action="portrait-focus-node" data-node-id="${escapeHtml(n.id)}" style="opacity:${em.toFixed(2)};--phase:${phase.toFixed(2)}s" tabindex="0" role="button" aria-label="${escapeHtml(n.label)}">
        <circle class="map-hit" cx="${pt.x.toFixed(1)}" cy="${pt.y.toFixed(1)}" r="14"/>
        ${n.stable || isFocused ? `<circle class="map-halo" cx="${pt.x.toFixed(1)}" cy="${pt.y.toFixed(1)}" r="${(r * 1.8).toFixed(1)}"/>` : ""}
        <circle class="map-dot" cx="${pt.x.toFixed(1)}" cy="${pt.y.toFixed(1)}" r="${r.toFixed(1)}"/>
        ${isFocused ? `<circle class="map-ring" cx="${pt.x.toFixed(1)}" cy="${pt.y.toFixed(1)}" r="${(r + 3).toFixed(1)}"/>` : ""}
      </g>`;
  }).join("");

  const labelText = model.themes.length <= 6 ? model.themes.map((theme) => {
    const at = labels.get(theme);
    if (!at) return "";
    const short = theme.length > 22 ? `${theme.slice(0, 21)}…` : theme;
    const active = following == null ? "" : (following === theme ? "is-active" : "is-dim");
    return `<text class="map-label ${active}" x="${at.x.toFixed(1)}" y="${at.y.toFixed(1)}" text-anchor="middle" data-action="portrait-select-theme" data-theme="${escapeHtml(theme)}" role="button">${escapeHtml(short.toUpperCase())}</text>`;
  }).join("") : "";

  const focusedNode = focused ? nodeById.get(focused) : null;
  return `
    <div class="portrait-map-wrap">
      <svg class="portrait-map" viewBox="0 0 ${MAP_W} ${MAP_H}" role="img" aria-label="Your portrait as a map: ${model.nodes.length} observations across ${model.themes.length} themes, ${model.edges.length} connections." data-action="portrait-map-clear">
        ${edges}${nodes}${labelText}
      </svg>
      ${renderStructureLine(model.thoughtCount, model.themes.length)}
      ${focusedNode ? `
        <button class="portrait-focus-line" data-action="portrait-open-claim" data-claim-id="${escapeHtml(focusedNode.id)}">
          <span class="portrait-focus-dot"></span>
          <span class="portrait-focus-text">${escapeHtml(focusedNode.label)}</span>
          <span class="portrait-focus-cta">See why ${chevron()}</span>
        </button>` : `<p class="portrait-caption">Tap a point to see it. Tap a theme to follow it.</p>`}
    </div>
  `;
}

function renderStructureLine(thoughtCount: number, themes: number): string {
  if (thoughtCount <= 0) return "";
  const text = themes >= 2
    ? `Built from ${thoughtCount} of your thoughts, across ${themes} themes.`
    : `Built from ${thoughtCount} of your thoughts.`;
  return `<p class="portrait-structure">${escapeHtml(text)}</p>`;
}

// ── The deck ────────────────────────────────────────────────────────────────

function renderDeck(): string {
  const p = state.portrait;
  const deck = deckClaims();
  if (deck.length === 0) {
    return `
      <div class="portrait-surface portrait-resting">
        <strong class="portrait-title">Nothing needs your word right now.</strong>
        <p class="portrait-body">Companion keeps only what you've agreed to, and shows where every piece came from.</p>
      </div>`;
  }
  const top = deck[0];
  const cards = deck.slice(0, 3).map((claim, position) => {
    const quote = firstExcerpt(claim);
    return `
      <article class="portrait-deck-card ${position === 0 ? "is-top" : ""} pos-${position}" data-claim-id="${escapeHtml(claim.id)}" data-key="deck:${escapeHtml(claim.id)}" ${position === 0 ? `data-action="portrait-open-claim"` : "aria-hidden=\"true\""}>
        <div class="portrait-deck-stamp" aria-hidden="true">Feels true</div>
        <p class="portrait-deck-statement">${escapeHtml(observationText(claim))}</p>
        ${quote ? `<p class="portrait-deck-quote">“${escapeHtml(quote)}”</p>` : ""}
        <div class="portrait-deck-meta"><span>${escapeHtml(lastMentionedLine(claim))}</span><span class="portrait-deck-why">See why ${chevron()}</span></div>
      </article>`;
  }).join("");
  return `
    <div class="portrait-deck-block">
      ${eyebrow("Needs your word", `<span class="portrait-count">${deck.length}</span>${p.lastDecidedId ? `<button class="portrait-undo" data-action="portrait-undo">Undo</button>` : ""}`)}
      <div class="portrait-deck">${cards}</div>
      <div class="portrait-deck-actions">
        <button class="portrait-round" data-action="portrait-not-quite" data-claim-id="${escapeHtml(top.id)}"><span class="portrait-round-glyph">✕</span><span>Not quite</span></button>
        <button class="portrait-round is-primary" data-action="portrait-decide" data-decision="confirm" data-claim-id="${escapeHtml(top.id)}"><span class="portrait-round-glyph">✓</span><span>Feels true</span></button>
      </div>
      <div class="portrait-deck-secondary">
        <button class="btn-ghost btn-sm" data-action="portrait-decide" data-decision="qualify" data-claim-id="${escapeHtml(top.id)}">Partly</button>
        <button class="btn-ghost btn-sm" data-action="portrait-later" data-claim-id="${escapeHtml(top.id)}">Later</button>
        <button class="btn-ghost btn-sm" data-action="set-tab" data-tab="companion">Open Companion</button>
      </div>
    </div>
  `;
}

// ── What it remembers ───────────────────────────────────────────────────────

function renderRemembered(): string {
  const p = state.portrait;
  const cards = rememberedCards();
  if (cards.length === 0) return "";
  const themes: string[] = [];
  cards.forEach((c) => { if (c.theme && !themes.includes(c.theme)) themes.push(c.theme); });
  const active = p.selectedTheme && cards.some((c) => c.theme === p.selectedTheme) ? p.selectedTheme : null;
  const shown = active ? cards.filter((c) => c.theme === active) : cards;
  const chips = themes.length >= 2 ? `
    <div class="portrait-chips">
      <button class="direction-chip ${active ? "" : "active"}" data-action="portrait-select-theme" data-theme="">Everything</button>
      ${themes.map((t) => `<button class="direction-chip ${active === t ? "active" : ""}" data-action="portrait-select-theme" data-theme="${escapeHtml(t)}">${escapeHtml(t)}</button>`).join("")}
    </div>` : "";
  return `
    <div class="portrait-remembered">
      ${eyebrow("What it remembers", `<button class="portrait-link" data-action="portrait-mode" data-mode="review">See all ${cards.length}</button>`)}
      ${chips}
      <div class="portrait-card-row">
        ${shown.slice(0, 12).map(({ claim, theme }) => `
          <button class="portrait-mini-card" data-action="portrait-open-claim" data-claim-id="${escapeHtml(claim.id)}" data-key="kept:${escapeHtml(claim.id)}">
            ${theme ? `<span class="portrait-mini-theme">${escapeHtml(theme.toUpperCase())}</span>` : ""}
            <span class="portrait-mini-statement">${escapeHtml(claim.statement)}</span>
            <span class="portrait-mini-meta">✓ ${escapeHtml(compactLabel(claim))}</span>
          </button>`).join("")}
        ${shown.length > 12 ? `<button class="portrait-mini-card portrait-mini-more" data-action="portrait-mode" data-mode="review"><span class="portrait-mini-statement">+${shown.length - 12}</span><span class="portrait-mini-meta">See all</span></button>` : ""}
      </div>
    </div>
  `;
}

function renderGrow(): string {
  return `
    <div class="portrait-grow">
      ${eyebrow("Grow your portrait")}
      <div class="portrait-surface">
        <p class="portrait-grow-prompt">${escapeHtml(growPrompt())}</p>
        <div class="portrait-grow-row">
          <span class="portrait-caption">It grows from what you write. Nothing is added without your word.</span>
          <button class="btn-primary btn-sm" data-action="portrait-grow">Write about this</button>
        </div>
      </div>
    </div>
  `;
}

function renderQuietRows(includeReview = true): string {
  const row = (title: string, subtitle: string, mode: string) => `
    <button class="portrait-row" data-action="portrait-mode" data-mode="${mode}">
      <span class="portrait-row-copy"><strong>${escapeHtml(title)}</strong>${subtitle ? `<span>${escapeHtml(subtitle)}</span>` : ""}</span>
      <span class="portrait-row-chevron">${chevron()}</span>
    </button>`;
  return `
    <div class="portrait-rows portrait-quiet-rows">
      ${includeReview ? row("Review what it remembers", "Everything it noticed, searchable", "review") : ""}
      ${row("Privacy & control", "Off, erase, and what Companion may use", "privacy")}
    </div>
  `;
}

// ── Review list ─────────────────────────────────────────────────────────────

function claimRow(claim: PortraitClaim): string {
  const waiting = awaitsWord(claim);
  const pending = state.portrait.pendingClaimIds.has(claim.id);
  return `
    <button class="portrait-claim-row ${waiting ? "is-waiting" : ""}" data-action="portrait-open-claim" data-claim-id="${escapeHtml(claim.id)}" data-key="claim:${escapeHtml(claim.id)}">
      <span class="portrait-claim-statement">${escapeHtml(claim.statement)}</span>
      <span class="portrait-claim-meta">${waiting ? "<i></i>" : (claim.confirmation.status === "confirmed" || claim.confirmation.status === "corrected" ? "✓ " : "")}${escapeHtml(compactLabel(claim))}${pending ? " · Updating…" : ""}</span>
      <span class="portrait-row-chevron">${chevron()}</span>
    </button>`;
}

function renderReview(): string {
  const p = state.portrait;
  const query = p.reviewSearch.trim().toLowerCase();
  const filtering = query.length > 0 || p.reviewFilter !== "all";
  const awaiting = p.claims.filter(awaitsWord).length;
  const matches = (claim: PortraitClaim) => {
    switch (p.reviewFilter) {
      case "needsWord": if (archiveGroup(claim) !== "suggested") return false; break;
      case "confirmed": if (archiveGroup(claim) !== "confirmed") return false; break;
      case "historical": if (!["superseded", "outdated", "rejected"].includes(claim.state)) return false; break;
      default: break;
    }
    if (!query) return true;
    return claim.statement.toLowerCase().includes(query) || (claim.userQualifier || "").toLowerCase().includes(query);
  };
  const chip = (id: typeof p.reviewFilter, label: string, count?: number) =>
    `<button class="direction-chip ${p.reviewFilter === id ? "active" : ""}" data-action="portrait-filter" data-filter="${id}">${escapeHtml(label)}${count ? ` <b>${count}</b>` : ""}</button>`;

  let body = "";
  if (filtering) {
    const rows = p.claims.filter(matches).sort((a, b) => b.temporal.lastEvidenceAt - a.temporal.lastEvidenceAt);
    body = rows.length ? rows.map(claimRow).join("") : `<div class="empty"><strong>Nothing here matches that.</strong></div>`;
  } else {
    const themed = new Set<string>();
    const sections = p.presentation.clusters.map((cluster) => {
      const ids = [...cluster.representativeIds, ...cluster.similarIds];
      ids.forEach((id) => themed.add(id));
      const rows = ids.map((id) => claimById(id)).filter((c): c is PortraitClaim => !!c);
      return `
        <div class="portrait-review-section">
          <div class="portrait-review-header"><span>${escapeHtml(cluster.title)}</span><small>${escapeHtml(cluster.temporalLabel)}</small></div>
          ${rows.map(claimRow).join("")}
        </div>`;
    }).join("");
    const rest = p.claims.filter((c) => !themed.has(c.id));
    const groups = new Map<string, PortraitClaim[]>();
    rest.forEach((c) => { const g = archiveGroup(c); groups.set(g, [...(groups.get(g) || []), c]); });
    const restSections = [...groups.entries()].map(([g, items]) => `
      <details class="portrait-review-section portrait-review-collapsed">
        <summary>${escapeHtml(ARCHIVE_GROUP_TITLES[g as keyof typeof ARCHIVE_GROUP_TITLES] || g)} (${items.length})</summary>
        ${items.map(claimRow).join("")}
      </details>`).join("");
    body = sections + restSections;
  }
  const confirmed = p.claims.filter((c) => archiveGroup(c) === "confirmed").length;
  const presentable = p.claims.filter((c) => ["confirmed", "suggested"].includes(archiveGroup(c))).length;
  const overview = confirmed > 0
    ? `${presentable} observations from your own thinking. You've confirmed ${confirmed}.`
    : `${presentable} observations from your own thinking. None count as true until you've seen them.`;
  return `
    <section class="panel portrait-panel">
      <div class="portrait-subheader">
        <button class="btn-icon" data-action="portrait-mode" data-mode="home" aria-label="Back">‹</button>
        <h2>Review what it remembers</h2>
      </div>
      <input class="portrait-search" type="search" data-field="portrait-search" placeholder="Search your observations" value="${escapeHtml(p.reviewSearch)}" />
      <p class="portrait-caption">${escapeHtml(overview)}</p>
      <div class="portrait-chips">
        ${chip("all", "Everything")}${chip("needsWord", "Needs your word", awaiting)}${chip("confirmed", "Has your word")}${chip("historical", "From before")}
      </div>
      <div class="portrait-review">${body}</div>
      ${filtering ? "" : `
        <div class="portrait-rows">
          <button class="portrait-row" data-action="portrait-mode" data-mode="privacy"><span class="portrait-row-copy"><strong>Privacy and deletion</strong></span><span class="portrait-row-chevron">${chevron()}</span></button>
        </div>
        <p class="portrait-caption">Everything here traces back to your own thoughts. LOT shows its working — it never defines you. Your corrections outrank LOT's observations. You can remove individual items or erase this understanding entirely.</p>`}
    </section>
  `;
}

// ── Privacy & control ───────────────────────────────────────────────────────

function renderPrivacy(): string {
  const p = state.portrait;
  return `
    <section class="panel portrait-panel">
      <div class="portrait-subheader">
        <button class="btn-icon" data-action="portrait-mode" data-mode="home" aria-label="Back">‹</button>
        <h2>Privacy &amp; control</h2>
      </div>
      ${eyebrow("Your data")}
      <div class="portrait-surface">
        <label class="pref-row">
          <span>Allow Companion to use guesses I haven't reviewed</span>
          <span class="switch"><input type="checkbox" data-action="portrait-toggle-unreviewed" ${p.unreviewedOptIn ? "checked" : ""} /><span class="switch-ui"></span></span>
        </label>
        <div class="portrait-actions-row">
          <button class="btn-secondary btn-sm" data-action="portrait-stop">Stop LOT from noticing</button>
          <button class="btn-danger btn-sm" data-action="portrait-erase">Turn off and erase everything</button>
        </div>
        <p class="portrait-caption">Off means Companion only uses what you've confirmed or corrected. Erasing deletes every observation and decision — your original thoughts are never touched.</p>
      </div>
    </section>
  `;
}

// ── Claim detail modal ──────────────────────────────────────────────────────

export function renderPortraitClaimModal(): string {
  const p = state.portrait;
  const claim = claimById(p.selectedClaimId);
  if (!claim) return "";
  const notesById = new Map(state.notes.map((n) => [n.id, n]));
  const evidence = claim.evidence
    .filter((e) => e.sourceType === "entry")
    .sort((a, b) => b.at - a.at)
    .map((e) => notesById.get(e.sourceId))
    .filter((n): n is NonNullable<typeof n> => !!n);
  const purpose = p.claimTextPurpose;
  const purposeCopy: Record<string, { title: string; hint: string; action: string }> = {
    correct: { title: "Say it your way", hint: "Rewrite this in your own words. Your version replaces LOT's.", action: "Save my words" },
    qualify: { title: "Partly true", hint: "What part is true, and what isn't? (optional)", action: "Save" },
    add_context: { title: "Add context", hint: "What would you add so this reads right?", action: "Save context" },
    contextualize: { title: "True, with a difference", hint: "When is this true — and when isn't it?", action: "Save" },
  };
  const hidden = claim.hiddenFromSurfaces;
  const historical = isHistorical(claim);
  return `
    <div class="modal-backdrop" data-action="portrait-close-claim">
      <div class="modal portrait-claim-modal" role="dialog" aria-modal="true" aria-labelledby="portrait-claim-title" data-action="">
        <div class="modal-drag-handle" aria-hidden="true"></div>
        <div class="modal-header">
          <h3 id="portrait-claim-title">Observation</h3>
          <button class="btn-icon modal-close-btn" data-action="portrait-close-claim" aria-label="Close">✕</button>
        </div>
        <div class="modal-section">
          <p class="portrait-claim-main">${escapeHtml(observationText(claim))}</p>
          ${claim.userQualifier ? `<p class="portrait-caption">You added: “${escapeHtml(claim.userQualifier)}”</p>` : ""}
          <div class="portrait-claim-facts">
            <span>${escapeHtml(provenanceLabel(claim))}</span>
            <span>${escapeHtml(stabilityDescription(claim))}</span>
            <span>${escapeHtml(evidenceSummary(claim, evidence.length))}</span>
          </div>
        </div>
        ${!historical ? `
        <div class="modal-section">
          ${purpose ? `
            <div class="portrait-claim-text">
              <strong>${escapeHtml(purposeCopy[purpose].title)}</strong>
              <textarea data-field="portrait-claim-text" placeholder="${escapeHtml(purposeCopy[purpose].hint)}">${escapeHtml(p.claimText)}</textarea>
              <div class="portrait-actions-row">
                <button class="btn-primary btn-sm" data-action="portrait-claim-action" data-claim-action="${purpose}" data-claim-id="${escapeHtml(claim.id)}">${escapeHtml(purposeCopy[purpose].action)}</button>
                <button class="btn-ghost btn-sm" data-action="portrait-claim-text-purpose" data-purpose="">Cancel</button>
              </div>
            </div>` : `
            <div class="portrait-actions-row portrait-claim-actions">
              ${awaitsWord(claim) ? `<button class="btn-primary btn-sm" data-action="portrait-claim-action" data-claim-action="confirm" data-claim-id="${escapeHtml(claim.id)}">Feels true</button>` : ""}
              <button class="btn-secondary btn-sm" data-action="portrait-claim-text-purpose" data-purpose="qualify">Partly true</button>
              <button class="btn-secondary btn-sm" data-action="portrait-claim-text-purpose" data-purpose="correct">Say it my way</button>
              <button class="btn-secondary btn-sm" data-action="portrait-claim-text-purpose" data-purpose="add_context">Add context</button>
              <button class="btn-secondary btn-sm" data-action="portrait-claim-text-purpose" data-purpose="contextualize">True, with a difference</button>
              ${awaitsWord(claim) || claim.confirmation.status !== "rejected" ? `<button class="btn-secondary btn-sm" data-action="portrait-claim-action" data-claim-action="reject" data-claim-id="${escapeHtml(claim.id)}">That's not me</button>` : ""}
              ${hidden
                ? `<button class="btn-ghost btn-sm" data-action="portrait-claim-action" data-claim-action="unhide" data-claim-id="${escapeHtml(claim.id)}">Show again</button>`
                : `<button class="btn-ghost btn-sm" data-action="portrait-claim-action" data-claim-action="hide" data-claim-id="${escapeHtml(claim.id)}">Hide</button>`}
              <button class="btn-danger btn-sm" data-action="portrait-erase-claim" data-claim-id="${escapeHtml(claim.id)}">Delete</button>
            </div>`}
        </div>` : ""}
        <div class="modal-section">
          <h4>What shaped this</h4>
          ${evidence.length === 0
            ? `<p class="portrait-caption">${escapeHtml(evidenceSummary(claim, 0))}</p>`
            : evidence.slice(0, 8).map((n) => `<blockquote class="portrait-evidence"><p>${escapeHtml(n.text.length > 400 ? `${n.text.slice(0, 397)}…` : n.text)}</p><small>${escapeHtml(n.createdAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }))}</small></blockquote>`).join("")}
          ${claim.counterEvidence.length ? `<p class="portrait-caption">Some of your thoughts point the other way.</p>` : ""}
        </div>
      </div>
    </div>
  `;
}

export function renderPortraitNotQuiteDialog(): string {
  const id = state.portrait.notQuiteClaimId;
  if (!id) return "";
  return `
    <div class="modal-backdrop" data-action="portrait-not-quite-close">
      <div class="modal portrait-sheet" role="dialog" aria-modal="true" aria-labelledby="portrait-nq-title">
        <div class="modal-drag-handle" aria-hidden="true"></div>
        <div class="modal-section">
          <h3 id="portrait-nq-title">No, not me?</h3>
          <p class="portrait-caption">Your answer permanently outranks what LOT noticed.</p>
          <div class="portrait-sheet-actions">
            <button class="btn-secondary" data-action="portrait-decide" data-decision="reject" data-claim-id="${escapeHtml(id)}">That's not me</button>
            <button class="btn-secondary" data-action="portrait-open-claim" data-claim-id="${escapeHtml(id)}">There's more to it</button>
            <button class="btn-ghost" data-action="portrait-later" data-claim-id="${escapeHtml(id)}">Leave it for now</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ── Root ────────────────────────────────────────────────────────────────────

export function renderPortraitView(): string {
  const p = state.portrait;
  if (!p.availabilityResolved || !p.consentResolved) return renderLoading();
  if (!p.available) {
    return `
      <section class="panel portrait-panel">
        <div class="panel-header"><div><h2>Your portrait</h2></div></div>
        <div class="portrait-surface"><strong class="portrait-title">Not open on your account yet.</strong><p class="portrait-body">LOT is opening the portrait gradually. It will appear here when it reaches you.</p></div>
      </section>`;
  }
  if (!p.enabled) return renderIntro();
  if (p.loading) return renderLoading();
  if (p.loadFailed) return renderFailed();
  if (p.claims.length === 0) return renderEarly();
  if (p.mode === "review") return renderReview();
  if (p.mode === "privacy") return renderPrivacy();
  const kept = keptCount();
  const waiting = deckClaims().length;
  return `
    <section class="panel portrait-panel portrait-home">
      <div class="panel-header"><div><h2>Your portrait</h2>${waiting || kept ? `<p class="panel-sub">${[waiting ? `${waiting} waiting for your word` : "", kept ? `${kept} kept in mind` : ""].filter(Boolean).join(" · ")}</p>` : ""}</div></div>
      ${renderMap()}
      ${renderDeck()}
      ${renderRemembered()}
      ${renderGrow()}
      ${renderQuietRows()}
    </section>
  `;
}
